// One example delivery, told status by status — the data behind both webhook
// docs widgets (WebhookPayloadExample and WebhookLifecycleExplorer), and the
// exact bodies the "send a test webhook" action POSTs at a sandbox endpoint.
//
// Field order and JSON types (string-typed numerics, string coordinates in
// addresses vs numbers in `location`) mirror the real sender,
// packages/curri-delivery-webhooks/src/curri.ts — and the service's test-send
// endpoint validates against that shape, so drift here fails loudly rather
// than sending something the docs never showed.

const DRIVER = {
  activeVehicle: {
    color: 'White',
    make: 'Ford',
    model: 'F-250',
    trim: 'XL',
    year: 2022,
  },
  firstName: 'Jordan',
  id: 'driver_8FhQ2LpXcW',
  lastName: 'Reyes',
  phoneNumber: '+13105550142',
}

const PICKUP_IMAGE = 'https://images.curri.com/delivery/del_k2VYQ3M9Rd/pickup-1.jpg'
const DROPOFF_IMAGE = 'https://images.curri.com/delivery/del_k2VYQ3M9Rd/dropoff-1.jpg'

interface Moment {
  /** One line under the status name in the lifecycle chart. */
  description: string
  /** One sentence about what changed in the payload at this moment. */
  caption: string
  scheduledAt: string | null
  deliveredAt: string | null
  cancellationReason: string | null
  /** Settled order total in cents, as a string — tracks `price` until it doesn't. */
  finalAdjustedPrice: string | null
  chargeType: 'delivery' | 'cancellation_fee' | 'no_charge' | null
  driver: Record<string, unknown>
  location: Record<string, number>
  images: string[]
}

const MOMENTS = {
  pending: {
    description: 'Booked; matching a driver',
    caption: 'Booked, and Curri is matching a driver. No driver or location yet.',
    scheduledAt: null,
    deliveredAt: null,
    cancellationReason: null,
    finalAdjustedPrice: '9800',
    chargeType: 'delivery',
    driver: {},
    location: {},
    images: [],
  },
  scheduled: {
    description: 'Booked for a future window',
    caption: "Booked for a future window; dispatch hasn't started, so scheduledAt is set.",
    scheduledAt: '2026-08-20T16:00:00.000Z',
    deliveredAt: null,
    cancellationReason: null,
    finalAdjustedPrice: '9800',
    chargeType: 'delivery',
    driver: {},
    location: {},
    images: [],
  },
  en_route_to_origin: {
    description: 'Driver heading to the pickup',
    caption: 'A driver is assigned and heading to the pickup — driver and location appear.',
    scheduledAt: null,
    deliveredAt: null,
    cancellationReason: null,
    finalAdjustedPrice: '9800',
    chargeType: 'delivery',
    driver: DRIVER,
    location: { latitude: 33.8689741, longitude: -118.2094378 },
    images: [],
  },
  at_origin: {
    description: 'At the pickup, loading',
    caption: 'The driver has arrived at the pickup and is loading.',
    scheduledAt: null,
    deliveredAt: null,
    cancellationReason: null,
    finalAdjustedPrice: '9800',
    chargeType: 'delivery',
    driver: DRIVER,
    location: { latitude: 33.8722501, longitude: -118.2201111 },
    images: [],
  },
  en_route_to_destination: {
    description: 'Driving to the destination',
    caption: 'Loaded and driving to the destination; a pickup photo is attached.',
    scheduledAt: null,
    deliveredAt: null,
    cancellationReason: null,
    finalAdjustedPrice: '9800',
    chargeType: 'delivery',
    driver: DRIVER,
    location: { latitude: 33.8451096, longitude: -118.1867343 },
    images: [PICKUP_IMAGE],
  },
  at_destination: {
    description: 'At the destination, unloading',
    caption: 'The driver has arrived at the destination and is unloading.',
    scheduledAt: null,
    deliveredAt: null,
    cancellationReason: null,
    finalAdjustedPrice: '9800',
    chargeType: 'delivery',
    driver: DRIVER,
    location: { latitude: 33.8177246, longitude: -118.1514023 },
    images: [PICKUP_IMAGE],
  },
  delivered: {
    description: 'Terminal — dropped off',
    caption: 'Terminal: deliveredAt is set and proof-of-delivery photos are attached.',
    scheduledAt: null,
    deliveredAt: '2026-08-19T16:41:33.000Z',
    cancellationReason: null,
    finalAdjustedPrice: '9800',
    chargeType: 'delivery',
    driver: DRIVER,
    location: { latitude: 33.8177246, longitude: -118.1514023 },
    images: [PICKUP_IMAGE, DROPOFF_IMAGE],
  },
  canceled: {
    description: 'Terminal — ended early',
    caption:
      'Terminal: the delivery ended without completing. cancellationReason says why, and finalAdjustedPrice drops to the cancellation fee while price keeps the original quote.',
    scheduledAt: null,
    deliveredAt: null,
    cancellationReason: 'User Requested',
    finalAdjustedPrice: '2500',
    chargeType: 'cancellation_fee',
    driver: DRIVER,
    location: { latitude: 33.8689741, longitude: -118.2094378 },
    images: [],
  },
} satisfies Record<string, Moment>

export type ExampleStatus = keyof typeof MOMENTS

export const EXAMPLE_STATUSES = Object.keys(MOMENTS) as ExampleStatus[]

export const momentFor = (status: ExampleStatus): Moment => MOMENTS[status]

/**
 * The full snapshot at a status, as the object the test-send endpoint accepts.
 * Key order matches the sender's object literal, so the example diffs cleanly
 * against a real request body.
 */
export const examplePayloadFor = (
  status: ExampleStatus
): Record<string, unknown> => {
  const moment = MOMENTS[status]
  return {
    accountLocation: 'Torrance Branch #114',
    cancellationReason: moment.cancellationReason,
    chargeType: moment.chargeType,
    createdAt: '2026-08-19T15:04:12.000Z',
    deliveredAt: moment.deliveredAt,
    deliveryMethod: 'truck',
    destination: {
      addressLine1: '4100 Donald Douglas Dr',
      addressLine2: '',
      city: 'Long Beach',
      latitude: '33.8177246',
      longitude: '-118.1514023',
      name: 'Long Beach Jobsite',
      postalCode: '90808',
      state: 'CA',
    },
    distance: '18240',
    driver: moment.driver,
    estimatedTravelTime: '1620',
    finalAdjustedPrice: moment.finalAdjustedPrice,
    id: 'del_k2VYQ3M9Rd',
    images: moment.images,
    isSandbox: true,
    location: moment.location,
    meta: {
      bolNumber: '88012345',
      dropoffNote: 'Call on arrival',
      orderNumber: 'SO-48213',
      pickupNote: 'Dock 4',
      poNumber: 'PO-77120',
    },
    origin: {
      addressLine1: '1200 W Walnut St',
      addressLine2: '',
      city: 'Compton',
      latitude: '33.8722501',
      longitude: '-118.2201111',
      name: 'PlumbSupply Compton',
      postalCode: '90220',
      state: 'CA',
    },
    price: '9800',
    scheduledAt: moment.scheduledAt,
    status,
    trackingUrl: 'https://app.curri.com/track/C-1f8a09b3c64d47e2a5b90c12d3e4f567',
  }
}

export const examplePayloadJsonFor = (status: ExampleStatus): string =>
  JSON.stringify(examplePayloadFor(status), null, 2)
